<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import { SERP_PROGRESS_LABEL, SERP_PROGRESS_TEXTS } from '../config/texts';
  import type { SearchProgressStage } from '../model/types';

  interface SerpSearchProgressProps {
    value: number;
    stage?: SearchProgressStage | null;
  }

  const props = withDefaults(defineProps<SerpSearchProgressProps>(), {
    stage: null,
  });

  const percent = computed<number>(() => Math.round(Math.min(Math.max(props.value, 0), 1) * 100));
  const caption = computed<string>(() => (props.stage ? SERP_PROGRESS_TEXTS[props.stage] : ''));
</script>

<template>
  <div
    class="serp-search-progress"
    data-testid="serp-search-progress"
  >
    <div
      class="serp-search-progress__track"
      role="progressbar"
      :aria-label="SERP_PROGRESS_LABEL"
      :aria-valuenow="percent"
      aria-valuemin="0"
      aria-valuemax="100"
      data-testid="serp-search-progress-track"
    >
      <div
        class="serp-search-progress__bar"
        :style="{ width: `${percent}%` }"
        data-testid="serp-search-progress-bar"
      />
    </div>

    <Typo
      v-if="caption"
      class="serp-search-progress__caption"
      :variant="TypoVariant.BodyMRegular"
      tag="span"
      data-testid="serp-search-progress-caption"
    >
      {{ caption }}
    </Typo>
  </div>
</template>

<style scoped lang="scss">
  .serp-search-progress {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    padding: 0 var(--spacing-m) var(--spacing-xs);
    width: 100%;

    &__track {
      background: var(--color-neutral-20);
      border-radius: var(--rounding-xxl);
      height: 6px;
      overflow: hidden;
      width: 100%;
    }

    &__bar {
      background: linear-gradient(
        45deg,
        var(--color-yellow-40) 0%,
        var(--color-orange-40) 57%,
        var(--color-orange-70) 100%
      );
      border-radius: inherit;
      height: 100%;
      transition: width 0.4s ease;
    }

    &__caption {
      color: var(--color-text-secondary);
    }
  }
</style>
